// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/views/jobs/searches/SlugSelect.js
//
// Sélecteur à AUTOCOMPLÉTION pour une liste fermée de slugs (famille de métier, secteur).
// On tape, la liste se filtre (sans tenir compte des accents ni de la casse) ; on peut aussi
// l'ouvrir et la parcourir, ou effacer la valeur. La valeur renvoyée est toujours le slug
// (ou '' si effacée), jamais le libellé traduit.
//
// Couleurs : variables CoreUI du thème (jamais de couleur en dur : thème tenant, mode sombre).
// Le menu est rendu dans <body> : une modale à défilement ne le coupe pas.

import React, { useMemo } from 'react'
import Select from 'react-select'
import { useTranslation } from 'react-i18next'

const Z_ABOVE_MODALS = 11000 // l'échelle du projet place `.modal` à 10500

const buildStyles = (size, invalid) => {
  const small = size === 'sm'
  return {
    control: (base, state) => ({
      ...base,
      minHeight: small ? 31 : 38,
      fontSize: small ? '0.875rem' : '1rem',
      background: 'var(--cui-body-bg)',
      borderColor: invalid
        ? 'var(--cui-form-invalid-border-color, var(--cui-danger))'
        : state.isFocused
          ? 'var(--cui-primary)'
          : 'var(--cui-border-color)',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(var(--cui-primary-rgb), 0.25)' : 'none',
      '&:hover': { borderColor: invalid ? 'var(--cui-danger)' : 'var(--cui-primary)' },
    }),
    valueContainer: (base) => ({ ...base, padding: small ? '0 8px' : '2px 12px' }),
    singleValue: (base) => ({ ...base, color: 'var(--cui-body-color)' }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: 'var(--cui-body-color)' }),
    placeholder: (base) => ({ ...base, color: 'var(--cui-secondary-color)' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: small ? 4 : 8,
      color: 'var(--cui-secondary-color)',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: small ? 4 : 8,
      color: 'var(--cui-secondary-color)',
    }),
    menu: (base) => ({
      ...base,
      background: 'var(--cui-body-bg)',
      border: '1px solid var(--cui-border-color)',
      boxShadow: '0 0.5rem 1.2rem rgba(0, 0, 0, 0.15)',
    }),
    menuPortal: (base) => ({ ...base, zIndex: Z_ABOVE_MODALS }),
    option: (base, state) => ({
      ...base,
      cursor: 'pointer',
      fontSize: small ? '0.875rem' : '1rem',
      color: state.isSelected ? '#fff' : 'var(--cui-body-color)',
      background: state.isSelected
        ? 'var(--cui-primary)'
        : state.isFocused
          ? 'var(--cui-tertiary-bg)'
          : 'transparent',
    }),
    noOptionsMessage: (base) => ({ ...base, color: 'var(--cui-secondary-color)' }),
  }
}

/**
 * @param {{id?: string, value: string, onChange: (slug: string) => void, values: string[],
 *          getLabel: (slug: string, t: Function) => string, placeholder?: string, size?: 'sm'|'md',
 *          invalid?: boolean, ariaLabel?: string}} props
 */
const SlugSelect = ({
  id,
  value,
  onChange,
  values,
  getLabel,
  placeholder,
  size = 'md',
  invalid = false,
  ariaLabel,
}) => {
  const { t, i18n } = useTranslation()

  // Triées par libellé dans la langue courante : en tapant, on retrouve ce qu'on cherche.
  const options = useMemo(
    () =>
      values
        .map((slug) => ({ value: slug, label: getLabel(slug, t) }))
        .sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
    [values, getLabel, t, i18n.language],
  )
  const styles = useMemo(() => buildStyles(size, invalid), [size, invalid])
  const selected = options.find((o) => o.value === value) || null

  return (
    <Select
      inputId={id}
      aria-label={ariaLabel}
      value={selected}
      onChange={(option) => onChange(option ? option.value : '')}
      options={options}
      placeholder={placeholder || t('searches:form.type_to_search')}
      noOptionsMessage={() => t('searches:form.no_match')}
      isClearable
      isSearchable
      styles={styles}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      menuPosition="fixed"
      classNamePrefix="ms-slug-select"
    />
  )
}

export default SlugSelect
